import type { Character, PlayerCharacter, SkillName, AbilityName } from "../types/entity.js";
import { DEATH_SAVE_NONE } from "../types/entity.js";
import { findClassData } from "../chargen/classData.js";
import { loadItem } from "../loaders/ItemLoader.js";
import { addItem, setEquipment, emptyInventory } from "../inventory/CharacterInventory.js";
import type { EquippableItem } from "../types/items.js";
import { InventoryStore } from "../../store/InventoryStore.js";
import { mintUuid } from "./partyIdentity.js";

/**
 * "The Blueberries" — a hardcoded Fighter / Rogue / Cleric / Wizard party
 * surfaced in the Launch adventure picker so you can drop into any
 * adventure without going through the chargen wizard. Great rolls —
 * primary stat 18, secondary 16, a couple of 14s.
 *
 * Each click of the Blueberries tile mints a fresh `partyId` via the
 * same `mintPartyId()` the reuse-party path uses, so two runs started
 * from the Blueberries tile save into separate slots.
 */

export const BLUEBERRIES_PARTY_NAME = "The Blueberries";

interface Blueprint {
  readonly name: string;
  readonly characterClass: string;
  readonly abilities: Character["abilities"];
  readonly skillProficiencies: readonly SkillName[];
  readonly saveProficiencies: readonly AbilityName[];
  readonly portrait: string;
  /**
   * Item ids loaded into this character's inventory at boot. Items with a
   * `slot` are auto-equipped via `equipBlueberries()`. See spec
   * `inventory-and-economy.md` § I19.
   */
  readonly startingEquipment: readonly string[];
  /** Spell ids known at L1. Casters only; martials leave empty. */
  readonly knownSpells?: readonly string[];
}

const BLUEPRINTS: readonly Blueprint[] = [
  {
    name: "Bram Tusk",
    characterClass: "fighter",
    abilities: { STR: 18, DEX: 14, CON: 16, INT: 10, WIS: 12, CHA: 10 },
    skillProficiencies: ["Athletics", "Intimidation"],
    saveProficiencies: ["STR", "CON"],
    portrait: "/engine/portraits/pool/human-male-fighter.png",
    startingEquipment: ["old-sword", "commoner-clothes"],
  },
  {
    name: "Hux Quill",
    characterClass: "rogue",
    abilities: { STR: 10, DEX: 18, CON: 14, INT: 14, WIS: 12, CHA: 14 },
    skillProficiencies: ["Stealth", "Perception"],
    saveProficiencies: ["DEX", "INT"],
    portrait: "/engine/portraits/pool/human-male-rogue.png",
    startingEquipment: ["shortbow", "commoner-clothes"],
  },
  {
    name: "Sister Ilka",
    characterClass: "cleric",
    abilities: { STR: 14, DEX: 12, CON: 16, INT: 10, WIS: 18, CHA: 14 },
    skillProficiencies: ["Insight", "Religion"],
    saveProficiencies: ["WIS", "CHA"],
    portrait: "/engine/portraits/pool/human-female-cleric.png",
    startingEquipment: ["holy-symbol", "commoner-clothes"],
    knownSpells: ["cure-wounds", "bless"],
  },
  {
    name: "Cobalt Vex",
    characterClass: "wizard",
    abilities: { STR: 10, DEX: 14, CON: 16, INT: 18, WIS: 14, CHA: 10 },
    skillProficiencies: ["Arcana", "Investigation"],
    saveProficiencies: ["INT", "WIS"],
    portrait: "/engine/portraits/pool/human-female-wizard.png",
    startingEquipment: ["arcane-spellbook", "commoner-clothes"],
    knownSpells: ["magic-missile", "shield", "mage-armor"],
  },
];

export function makeBlueberriesParty(): Character[] {
  return BLUEPRINTS.map(fromBlueprint);
}

/**
 * Load each blueprint's `startingEquipment` items into `InventoryStore` for
 * the corresponding party member, auto-equipping any with a `slot` field.
 *
 * Call this once after `PartyStore.setMembers(makeBlueberriesParty())` so
 * the instanceIds line up. Members are matched by `name` (the Blueberries
 * roster has no duplicates). Missing items are skipped silently — content
 * gaps shouldn't crash the boot.
 */
export async function equipBlueberries(members: readonly PlayerCharacter[]): Promise<void> {
  for (const bp of BLUEPRINTS) {
    const member = members.find((m) => m.name === bp.name);
    if (!member) continue;

    let inv = emptyInventory();
    for (const itemId of bp.startingEquipment) {
      const item = await loadItem(itemId);
      if (!item) continue;

      const result = addItem(inv, item, 1, member.abilities.STR);
      inv = result.inventory;

      if (item.slot && result.added > 0) {
        inv = setEquipment(inv, item.slot, item as EquippableItem);
      }
    }
    InventoryStore.set(member.instanceId, inv);
  }
}

function fromBlueprint(bp: Blueprint): Character {
  const classData = findClassData(bp.characterClass);
  const maxHp = classData?.maxHp ?? 10;
  const armorClass = classData?.ac ?? 12;
  const spellSlots: Record<number, number> = { ...(classData?.spellSlots ?? {}) };

  return {
    kind: "player",
    instanceId: `pc-${mintUuid()}`,
    dataId: bp.characterClass,
    name: bp.name,
    characterClass: bp.characterClass,
    race: "human",
    background: "adventurer",
    portrait: bp.portrait,
    level: 1,
    position: { x: 0, z: 0 },
    hp: maxHp,
    maxHp,
    armorClass,
    speed: { walk: 30 },
    abilities: { ...bp.abilities },
    proficiencyBonus: 2,
    spellSlots,
    xp: 0,
    knownSpells: [...(bp.knownSpells ?? [])],
    reactions: [],
    skillProficiencies: new Set(bp.skillProficiencies),
    saveProficiencies: new Set(bp.saveProficiencies),
    expertiseSkills: new Set(),
    conditions: new Set(),
    deathSaves: DEATH_SAVE_NONE,
    formationSlot: null,
  };
}
